import React from "react";
import uniqBy from "lodash/uniqBy";

import { Action, Category } from "@/constants/events";
import { Deal } from "@/custom-types/Deals";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useDispatch, useSelector } from "@/redux/hooks";
import { getUserId } from "@/redux/selectors/user";
import { dealCardUpdateMedRec } from "@/utils/dealCardUpdateMedRec";
import { getDispensaryUrl } from "@/utils/dispensaryUrls";
import { getRoundedDistance } from "@/utils/distance";

import Carousel from "@/components/botanic/Carousel";
import DealCard from "@/components/botanic/DealCard";

const CartCarousel: React.FC<{ cartDeals: Deal[] }> = ({ cartDeals }) => {
  const domainCountryCode = useDomainCountryCode();
  const userId = useSelector(getUserId);
  const dispatch = useDispatch();
  const { publishEvent } = useEventTracker();
  if (!cartDeals.length) {
    return null;
  }

  const uniqueDispensaryDeals = uniqBy(cartDeals, "dispensary.name");
  // Use unique dispensary deals if there are at least 3 to show
  const displayedDeals =
    uniqueDispensaryDeals.length >= 3
      ? uniqueDispensaryDeals
      : cartDeals.slice(0, 8);

  const cardClick = async (deal: Deal) => {
    const {
      dispensary: { id: dispensaryId, distanceMi },
    } = deal;

    publishEvent({
      action: Action.click,
      category: Category.dealsCartCard,
      dealId: deal.id,
      dispensaryDistance: getRoundedDistance(distanceMi, domainCountryCode),
      dispensaryId,
      label: `Dispensary deals`,
      userId: userId as number,
    });

    // Med/rec deals will have medical and/or recreational properties, check if we need to update the user preference
    if (deal.isMedical || deal.isRecreational) {
      await dealCardUpdateMedRec(deal, dispatch);
    }
  };

  return (
    <Carousel
      className="mb-section"
      title={
        <h2 className="heading--m">Promo codes, weed coupons, & promotions</h2>
      }
      subtitle="First time, senior, veteran, & student deals"
    >
      {displayedDeals.map((deal) => {
        const {
          title,
          dispensary: { slug },
        } = deal;
        const isDealCBD = !deal.isMedical && !deal.isRecreational;
        const dealType = deal.isMedical && !deal.isRecreational ? "Med" : "Rec";

        const dealUrl = getDispensaryUrl({
          countryCode: domainCountryCode,
          query: {
            deal_title: [title],
            ...(!isDealCBD && { menu_type: dealType }),
          },
          slug,
          tab: "menu",
        });

        return (
          <DealCard
            className="rounded h-full overflow-hidden shadow-low"
            countryCode={domainCountryCode}
            deal={{ ...deal, dealUrl }}
            disableLazyLoad={!!cartDeals.length}
            key={deal.id}
            onClick={() => cardClick(deal)}
            showFulfillment={true}
          />
        );
      })}
    </Carousel>
  );
};

export default CartCarousel;
